import React, { useMemo, useState } from "react";
import { Box, Typography, Link, Divider, Button } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import "../../Footer/Footer.css";
import { ASSETS_BASE_URL, COMPANY_ADDRESS, COMPANY_SOCIAL1, COMPANY_SOCIAL2, COMPANY_SOCIAL3, COMPANY_SOCIAL4 } from "../../../config";
import { useTranslation } from "react-i18next";
import Language from "../../../Language/language";
import { LABELS, UI_TEXT } from "../../../constants";
import { useNavigate } from "react-router-dom";
import { FaFacebook, FaLinkedin, FaYoutube } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { BiPhoneCall } from "react-icons/bi";
import { useCompliance } from "../../../context/complianceContext";
import ComplianceDescriptionModal from "../../CompliancePolicy/ComplianceDescriptionModal";
import EulaLibraryModal from "../../CompliancePolicy/EulaLibraryModal";
import { openComplianceDocument } from "../../CompliancePolicy/complianceUtils";
import useBrandingAssets from "../../../hooks/useBrandingAssets";
import CompanyLogo from "../../CompanyLogo/CompanyLogo";
import useCompanyInfo from "../../../hooks/useCompanyInfo";
import { useOptions } from "../../../context/optionContext";

export const REGION_PHONE_NUMBERS = [
        { regionKey: "FOOTER.REGIONS.NORTH_AMERICA", phone: "(+1) 804-342-9700" },
        { regionKey: "FOOTER.REGIONS.CHINA", phone: "(+86) 150-2244-2472" },
        { regionKey: "FOOTER.REGIONS.GREATER_ASIA", phone: "(+65) 8128-4044" },
        { regionKey: "FOOTER.REGIONS.EMEA", phone: "(+31) 183-627-555" },
        { regionKey: "FOOTER.REGIONS.LATAM", phone: "(+52) 229-915-3501" },
    ];

const Footer = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { items } = useCompliance();
    const { footerLogoEnabled } = useBrandingAssets();
    const { companyName, companyEmail, companyAddress, companyPhone, companySocial1, companySocial2, companySocial3, companySocial4 } = useCompanyInfo();
    const { optionKeys } = useOptions();
    const showLanguageSelector = optionKeys?.enableLanguageTranslation !== "false";
    const showFooterVersion = optionKeys?.footerShowVersion !== "false";
    const showFooterSupport = optionKeys?.footerShowSupport !== "false";
    const showFooterTerms = optionKeys?.footerShowTerms !== "false";
    const showFooterEula = optionKeys?.footerShowEula !== "false";
    const showFooterPrivacy = optionKeys?.footerShowPrivacy !== "false";
    const showFooterCookiePolicy = optionKeys?.footerShowCookiePolicy !== "false";
    const showFooterManual = optionKeys?.footerShowManual !== "false";
    const eulaDocs = items.EULA || [];
    const privacyDoc = items.PRIVACY || null;
    const termsDoc = items.TERMS || null;
    const supportDoc = items.SUPPORT || null;
    const cookieDoc = items.COOKIE || null;
    const [policyModal, setPolicyModal] = useState({ open: false, doc: null });
    const [eulaModalOpen, setEulaModalOpen] = useState(false);
    const quickLinks = [
        { label: t('LABELS.HOME'), path: "/" },
        { label: t('LABELS.MACHINESS'), path: "/machines" },
        { label: t('LABELS.RSPL'), path: "/rspl" },
        { label: t('LABELS.RECOMMENDATION'), path: "/recommendations" },
    ];

    const quickActions = [
        { label: t('LABELS.SEARCH'), path: "/search-results" },
        { label: t('LABELS.TRACK_ORDER'), path: "/track-order" },
        { label: t('LABELS.CONTACT_CCR'), path: "/support" },
    ]

    const legalLinks = useMemo(
        () => [
            { id: "support", label: t('LABELS.SUPPORT'), doc: supportDoc, fallback: "https://support.ithena.io/" },
            { id: "terms", label: t('TITLES.TERMS_AND_CONDITIONS'), doc: termsDoc, fallback: "https://privacy.ithena.io/terms" },
            { id: "privacy", label: t('LABELS.PRIVACY_POLICY'), doc: privacyDoc, fallback: "https://privacy.ithena.io/" },
            { id: "cookie", label: t('LABELS.COOKIE_POLICY', { defaultValue: 'Cookie Policy' }), doc: cookieDoc, fallback: "https://privacy.ithena.io/" },
        ],
        [supportDoc, termsDoc, privacyDoc, cookieDoc, t]
    );

    const visibleLegalLinks = useMemo(
        () =>
            legalLinks.filter((link) => {
                if (link.id === "support") return showFooterSupport;
                if (link.id === "terms") return showFooterTerms;
                if (link.id === "privacy") return showFooterPrivacy;
                if (link.id === "cookie") return showFooterCookiePolicy;
                return true;
            }),
        [legalLinks, showFooterTerms, showFooterPrivacy, showFooterSupport, showFooterCookiePolicy]
    );

    const handlePolicyClick = (item) => {
        openComplianceDocument({
            document: item.doc,
            fallbackUrl: item.fallback,
            onShowDescription: (document) =>
                setPolicyModal({ open: true, doc: document }),
        });
    };

    const handleEulaClick = () => {
        if (!eulaDocs.length) {
            openComplianceDocument({
                document: null,
                fallbackUrl: "https://eula.ithena.io/",
                onShowDescription: (document) =>
                    setPolicyModal({ open: true, doc: document }),
            });
            return;
        }
        if (eulaDocs.length === 1) {
            openComplianceDocument({
                document: eulaDocs[0],
                fallbackUrl: "https://eula.ithena.io/",
                onShowDescription: (document) =>
                    setPolicyModal({ open: true, doc: document }),
            });
            return;
        }
        setEulaModalOpen(true);
    };

    const contactInfo = [
        { icon: <LocationOnIcon className="footer-icon" />, text: companyAddress || COMPANY_ADDRESS },
        { icon: <PhoneIcon className="footer-icon" />, text: companyPhone, isPhone: true },
        { icon: <EmailIcon className="footer-icon" />, text: companyEmail, isEmail: true },
    ];

    const socialIcons = [
        {
            name: t('SOCIAL_MEDIA.FACEBOOK'),
            icon: <FaFacebook />,
            href: companySocial1 || COMPANY_SOCIAL1
        },
        {
            name: t('SOCIAL_MEDIA.LINKEDIN'),
            icon: <FaLinkedin />,
            href: companySocial2 || COMPANY_SOCIAL2
        },
        {
            name: t('SOCIAL_MEDIA.TWITTER'),
            icon: <FaXTwitter />,
            href: companySocial3 || COMPANY_SOCIAL3
        },
        {
            name: t('SOCIAL_MEDIA.YOUTUBE'),
            icon: <FaYoutube />,
            href: companySocial4 || COMPANY_SOCIAL4
        }
    ];


    return (
        <Box
            component="footer"
            pt={1}
            pb={0.75}
            px={{ xs: 1, md: 2 }}
            sx={{ backgroundColor: "#f2f2f2", borderTop: "1px solid #dfdfdf" }}
        >
            <Box
                sx={{
                    mx: "auto",
                    px: { xs: 1.25, md: 2.25 },
                    py: { xs: 1.25, md: 1.5 },
                    display: "flex",
                    alignItems: "stretch",
                    columnGap: { xs: 1.25, md: 2 },
                    flexDirection: { xs: "column", md: "row" },
                }}
            >
                <Box
                    sx={{
                        width: { xs: "100%", md: 165 },
                        borderRight: { xs: "none", md: "1px solid #dfdfdf" },
                        pr: { xs: 1.5, md: 1.5 },
                        pb: { xs: 1.25, md: 0 },
                        mb: { xs: 1.25, md: 0 },
                        borderBottom: { xs: "1px solid #dfdfdf", md: "none" },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between",
                        rowGap: 1.2,
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", rowGap: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "130px" }}>
                            <CompanyLogo variant="footerPrimary" alt="logo" onClick={() => navigate("/home")} style={{ cursor: "pointer", maxWidth: "122px" }} skeletonWidth={122} skeletonHeight={34} />
                        </Box>
                    </Box>

                    <Box display="flex" columnGap={0.85} justifyContent="center" alignItems="center" sx={{ mt: 0.3 }}>
                        {socialIcons.map((social, index) => (
                            <Button
                                key={index}
                                variant="roundedGreyBgSquareContained"
                                color="#aaa"
                                component="a"
                                href={social.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={social.name}
                            >
                                {social.icon}
                            </Button>
                        ))}
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "76px" }}>
                        {footerLogoEnabled && (
                            <CompanyLogo
                                variant="secondary"
                                alt="logo"
                                onClick={() => window.open("https://www.mklgroup.com/what-we-do/industrial", "_blank")}
                                style={{ cursor: "pointer", maxWidth: "70px" }}
                                skeletonWidth={70}
                                skeletonHeight={24}
                            />
                        )}
                    </Box>
                </Box>

                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", rowGap: 1.25 }}>
                    <Typography
                        className="footer-title"
                        sx={{
                            textTransform: "uppercase",
                            fontSize: { xs: "0.92rem", md: "1.02rem" },
                            letterSpacing: "0.03em",
                            lineHeight: 1.2,
                            fontWeight: 800,
                            textAlign: "left",
                            px: { xs: 1, md: 1.1 },
                        }}
                    >
                        {t("FOOTER.TAGLINE")}
                    </Typography>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" },
                        }}
                    >
                        {REGION_PHONE_NUMBERS.map((info, index) => (
                            <Box
                                key={info.regionKey}
                                sx={{
                                    px: { xs: 1, md: 1.1 },
                                    py: 0.8,
                                    borderRight: {
                                        xs: "none",
                                        lg: index === REGION_PHONE_NUMBERS.length - 1 ? "none" : "1px solid #e2e2e2",
                                    },
                                    borderBottom: {
                                        xs: index === REGION_PHONE_NUMBERS.length - 1 ? "none" : "1px solid #e2e2e2",
                                        sm: "none",
                                        lg: "none",
                                    },
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", columnGap: 0.6, mb: 0.2 }}>
                                    <BiPhoneCall size={15} color="#444" />
                                    <Typography
                                        fontWeight={800}
                                        textTransform="uppercase"
                                        sx={{ fontSize: { xs: "0.82rem", md: "0.86rem" }, lineHeight: 1.2 }}
                                    >
                                        {t(info.regionKey)}
                                    </Typography>
                                </Box>
                                <Link
                                    href={`tel:${info.phone}`}
                                    underline="none"
                                    sx={{
                                        textTransform: "uppercase",
                                        fontSize: { xs: "1rem", md: "1.08rem" },
                                        fontWeight: 700,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {info.phone}
                                </Link>
                            </Box>
                        ))}
                    </Box>

                    <Box
                        sx={{
                            px: { xs: 1, md: 1.1 },
                            py: 0.75,
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={700}
                            textTransform="uppercase"
                            sx={{ fontSize: "0.7rem", lineHeight: 1.2, mb: 0.35 }}
                        >
                            {t("FOOTER.CONFIDENTIAL_NOTICE_TITLE")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.7rem", lineHeight: 1.25 }}>
                            {t("FOOTER.CONFIDENTIAL_NOTICE_BODY", { company: companyName })}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Divider and Copyright */}
            <Divider sx={{ mb: 1.2, mt: 0.4 }} variant="middle" />
            <Box textAlign="center" sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', columnGap: 0.7, mb: 0.5, flexWrap: 'wrap', rowGap: 0.25 }}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.9rem' }}>
                    {t('FOOTER.ALL_RIGHTS_RESERVED', { year: new Date().getFullYear(), company: companyName })}
                </Typography>
                {(() => {
                    const footerItems = [
                        showFooterVersion && {
                            id: "version",
                            node: (
                                <Link color="textSecondary" underline="hover" href={"/admin?tab=version_info"}>
                                    {t('LABELS.VERSION')}
                                </Link>
                            ),
                        },
                        ...visibleLegalLinks.map((item) => ({
                            id: `legal-${item.id}`,
                            node: (
                                <Link
                                    component="button"
                                    type="button"
                                    color="textSecondary"
                                    underline="hover"
                                    onClick={() => handlePolicyClick(item)}
                                >
                                    {item.label}
                                </Link>
                            ),
                        })),
                        showFooterEula && {
                            id: "eula",
                            node: (
                                <Link
                                    component="button"
                                    type="button"
                                    color="textSecondary"
                                    underline="hover"
                                    onClick={handleEulaClick}
                                >
                                    {t('LABELS.EULA')}
                                </Link>
                            ),
                        },
                        showFooterManual && {
                            id: "manual",
                            node: (
                                <Link color="textSecondary" underline="hover" href={`${ASSETS_BASE_URL}/manual.pdf`}>
                                    {t('LABELS.MANUAL')}
                                </Link>
                            ),
                        },
                    ].filter(Boolean);

                    return footerItems.map((item, index) => (
                        <React.Fragment key={item.id}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.88rem' }}>
                                {index === 0 ? "" : "| "}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.88rem' }}>
                                {item.node}
                            </Typography>
                        </React.Fragment>
                    ));
                })()}
                {showLanguageSelector && (
                    <>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.88rem' }}>|</Typography>
                        <Language />
                    </>
                )}
            </Box>
            <ComplianceDescriptionModal
                open={policyModal.open}
                setOpen={(open) =>
                    setPolicyModal((prev) => ({ open, doc: open ? prev.doc : null }))
                }
                document={policyModal.doc}
            />
            <EulaLibraryModal
                open={eulaModalOpen}
                setOpen={setEulaModalOpen}
                documents={eulaDocs}
            />
        </Box>
    );
};

export default Footer;